import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { gameStateAPI, type GameState, type SaveGameStateData } from '../services/api'
import { useAuthStore } from '../stores/authStore'

/**
 * Load game state for a specific puzzle
 */
export function useGameState(puzzleId: string) {
  const { isAuthenticated, sessionChecked } = useAuthStore()
  const ready = sessionChecked && isAuthenticated

  return useQuery<GameState | null>({
    queryKey: ['gameState', puzzleId],
    queryFn: () => gameStateAPI.load(puzzleId),
    enabled: ready && !!puzzleId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Load all game states for the current user
 */
export function useAllGameStates() {
  const { isAuthenticated, sessionChecked } = useAuthStore()
  const ready = sessionChecked && isAuthenticated

  return useQuery<GameState[]>({
    queryKey: ['gameStates', 'all'],
    queryFn: async () => {
      return await gameStateAPI.loadAll()
    },
    enabled: ready,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Save game state mutation
 */
export function useSaveGameState() {
  const { isAuthenticated, sessionChecked } = useAuthStore()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: SaveGameStateData) => {
      if (!sessionChecked || !isAuthenticated) return null
      return await gameStateAPI.save(data)
    },
    onSuccess: (_, variables) => {
      // Invalidate queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['gameState', variables.puzzleId] })
      queryClient.invalidateQueries({ queryKey: ['gameStates', 'all'] })
    },
  })
}
